import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: 'autoglas-reparatur'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      service: 'autoglas-reparatur'
    });
  };

  return (
    <>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Kontakt
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Kontaktieren Sie uns für professionelle Autoglas Reparatur
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Kontaktformular
              </h2>
              <p className="text-gray-600 mb-8">
                Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-lg p-6 text-center"
                >
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">
                    Nachricht gesendet!
                  </h3>
                  <p className="text-green-700">
                    Vielen Dank für Ihre Nachricht. Wir werden uns schnellstmöglich bei Ihnen melden.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Ihr vollständiger Name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        E-Mail *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="ihre.email@beispiel.de"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefonnummer
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0621 71895675"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Gewünschte Leistung
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="autoglas-reparatur">Autoglas Reparatur</option>
                      <option value="autoglas-austausch">Autoglas Austausch</option>
                      <option value="scheibenversiegelung">Scheibenversiegelung</option>
                      <option value="notfall-service">Notfall Service</option>
                      <option value="mobile-reparatur">Mobile Reparatur</option>
                      <option value="beratung">Beratung</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Nachricht *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2"
                  >
                    <Send size={20} />
                    <span>Nachricht senden</span>
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Kontaktinformationen
              </h2>
              <p className="text-gray-600 mb-8">
                Kontaktieren Sie uns direkt oder besuchen Sie uns in einem unserer Standorte.
              </p>

              <div className="space-y-6">
                {/* Phone */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Telefon</h3>
                    <a
                      href="tel:062171895675"
                      className="text-blue-600 hover:text-blue-700 text-lg font-medium"
                    >
                      0621 71895675
                    </a>
                    <p className="text-gray-600 text-sm mt-1">
                      Mo-Fr: 8:00 - 18:00 Uhr
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">E-Mail</h3>
                    <a
                      href="mailto:info@autoglass-mannheim.de"
                      className="text-blue-600 hover:text-blue-700 text-lg font-medium"
                    >
                      info@autoglass-mannheim.de
                    </a>
                    <p className="text-gray-600 text-sm mt-1">
                      Antwort innerhalb von 24 Stunden
                    </p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Standorte</h3>
                    <div className="space-y-2">
                      <div>
                        <p className="font-medium text-gray-900">Mannheim</p>
                        <p className="text-gray-600 text-sm">Hauptstandort</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Frankfurt</p>
                        <p className="text-gray-600 text-sm">Mobile Reparatur</p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Stuttgart</p>
                        <p className="text-gray-600 text-sm">Mobile Reparatur</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Opening Hours */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Öffnungszeiten</h3>
                    <div className="space-y-1 text-gray-600">
                      <p>Montag - Freitag: 8:00 - 18:00 Uhr</p>
                      <p>Samstag: 9:00 - 14:00 Uhr</p>
                      <p>Sonntag: Geschlossen</p>
                      <p className="text-blue-600 font-medium mt-2">Notfallservice: 24/7</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-red-800 mb-2">
                  Notfallservice
                </h3>
                <p className="text-red-700 mb-4">
                  Bei dringenden Autoglas Reparaturen sind wir 24/7 für Sie erreichbar.
                </p>
                <a
                  href="tel:062171895675"
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center space-x-2"
                >
                  <Phone size={20} />
                  <span>Notfall anrufen</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Unsere Servicegebiete
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wir sind für Sie da in Mannheim, Frankfurt und Stuttgart
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                city: "Mannheim",
                description: "Unser Hauptstandort in Mannheim bietet alle Autoglas Dienstleistungen.",
                services: ["Werkstatt Service", "Mobile Reparatur", "Notfallservice", "Beratung"],
                contact: "0621 71895675"
              },
              {
                city: "Frankfurt",
                description: "Mobile Autoglas Reparatur in Frankfurt am Main und Umgebung.",
                services: ["Mobile Reparatur", "Vor-Ort Service", "Notfallservice", "Flexible Termine"],
                contact: "0621 71895675"
              },
              {
                city: "Stuttgart",
                description: "Professionelle Autoglas Reparatur in Stuttgart und Region.",
                services: ["Mobile Reparatur", "Komplettservice", "Schneller Service", "Qualitätsgarantie"],
                contact: "0621 71895675"
              }
            ].map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-sm"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <MapPin size={24} className="text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {area.city}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {area.description}
                </p>
                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-900">Leistungen:</h4>
                  <ul className="space-y-2">
                    {area.services.map((service, serviceIndex) => (
                      <li key={serviceIndex} className="flex items-center space-x-2 text-gray-700">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                        <span className="text-sm">{service}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={`tel:${area.contact}`}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {area.contact}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für Ihre Autoglas Reparatur?
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Kontaktieren Sie uns jetzt für ein kostenloses Angebot oder einen Termin.
            </p>
            <a
              href="tel:062171895675"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors duration-300 inline-flex items-center space-x-2"
            >
              <Phone size={24} />
              <span>Jetzt anrufen: 0621 71895675</span>
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact; 